/**
 * A loose relationship's `sourceFilter` / `targetFilter` as a predicate on one
 * side of a JOIN — the same assembly as `buildFilterWhereClause` (each group's
 * conditions OR-ed, groups AND-ed) with every field qualified by the side's
 * alias, and the whole predicate parenthesised so it can be ANDed onto an ON
 * clause (`… AND (c."target_type" = 'post')`). Kept in the ON rather than a
 * WHERE so an outer join keeps its unmatched rows. Returns '' when no
 * condition is active. `columnOf` finds a filtered field among the side's
 * columns: the field is written in that column's catalog spelling (a MetaInfo
 * declaration may spell `kind` for Oracle's `KIND`), and a date / timestamp
 * value as the engine's date literal (`sqlTemporalLiteral` — Oracle TO_DATE),
 * like the WHERE builders do. A field no column resolves is written as
 * declared — the declaration's own catalog spelling (`declaredCatalogSpelling`).
 */

import type { DatabaseEngineId } from '../../../../core/engines';
import type { FilterGroup } from '../../core/types/workspaceTypes';
import { quoteIdent } from '../../data-profile/utils/sqlIdentifierQuoting';
import { buildConditionSql } from './filterWhereBuilder';

/** A filtered column as its relation holds it: the catalog spelling and the data type. */
export type FilterColumn = { readonly name: string; readonly dataType?: string };

export function buildQualifiedFilterSql(
  filters: readonly FilterGroup[] | undefined,
  qualifierSql: string,
  engine: DatabaseEngineId,
  columnOf?: (field: string) => FilterColumn | undefined,
): string {
  if (!filters || filters.length === 0) return '';
  const nameOf = (field: string): string => columnOf?.(field)?.name ?? field;
  const groups = filters
    .filter((g) => g.conditions.length > 0)
    .map((g) => {
      const field = `${qualifierSql}.${quoteIdent(engine, nameOf(g.field))}`;
      // A sibling column (a declared lat/lng pair's longitude) takes the same qualifier.
      const quoteSiblingField = (name: string): string => `${qualifierSql}.${quoteIdent(engine, nameOf(name))}`;
      const valueDataType = columnOf?.(g.field)?.dataType;
      const parts = g.conditions
        .map((c) => buildConditionSql(field, c, engine, { quoteSiblingField, valueDataType }))
        .filter((sql) => sql.length > 0);
      return { sql: parts.length > 1 ? `(${parts.join(' OR ')})` : (parts[0] ?? ''), grouped: parts.length > 1 };
    })
    .filter((g) => g.sql.length > 0);
  if (groups.length === 0) return '';
  if (groups.length === 1 && groups[0]!.grouped) return groups[0]!.sql;
  return `(${groups.map((g) => g.sql).join(' AND ')})`;
}

/**
 * `columnOf` for {@link buildQualifiedFilterSql} over a relation's columns:
 * the filtered field by its exact spelling, else the only column that matches
 * it case-insensitively. A case-distinct sibling is never taken for it
 * (Oracle's `"created_at"` and `CREATED_AT` are two columns); an ambiguous
 * fold resolves nothing, so the value stays a plain literal.
 */
export function filterColumnLookup(
  columns: readonly FilterColumn[] | undefined,
): (field: string) => FilterColumn | undefined {
  return (field) => {
    const exact = columns?.find((c) => c.name === field);
    if (exact) return exact;
    const folded = columns?.filter((c) => c.name.toLowerCase() === field.toLowerCase()) ?? [];
    return folded.length === 1 ? folded[0] : undefined;
  };
}
